import { HttpClient, HttpContext, HttpResourceRef, httpResource } from "@angular/common/http";
import { Injectable, Signal, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CACHE_CONFIG, QueryOptions } from "@kno2/core/http";
import { filterNonEmptyProperties } from "@kno2/utils";
import { firstValueFrom, map } from "rxjs";
import { ColorCode, Reseller, ResellerType, ResellerUrlCode, ResellersPagedResult } from "./resellers.models";

@Injectable({
    providedIn: "root"
})
export class ResellersService {
    private httpClient = inject(HttpClient);

    public getResellers(query: Signal<QueryOptions>): HttpResourceRef<ResellersPagedResult> {
        return httpResource(() => ({
            url: "/api/resellers",
            params: filterNonEmptyProperties({ ...query() })
        }));
    }

    public getAllResellers = () =>
        toSignal(
            this.httpClient
                .get<{ resellers: Reseller[] }>("/api/resellers", {
                    params: {
                        pageSize: 5000
                    },
                    context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
                })
                .pipe(map(({ resellers }) => resellers)),
            {
                initialValue: []
            }
        );

    public getAllResellersAsync = (): Promise<Reseller[]> =>
        firstValueFrom(
            this.httpClient.get<{ resellers: Reseller[] }>("/api/resellers?pageSize=5000", {
                context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
            })
        ).then((response) => response.resellers);

    public getResellerByIdAsync = (resellerId: string): Promise<Reseller> => firstValueFrom(this.httpClient.get<Reseller>(`/api/resellers/${resellerId}`));

    public getUrlCodeAsync = (): Promise<ResellerUrlCode> => firstValueFrom(this.httpClient.get<ResellerUrlCode>("/api/resellers/urlcode"));

    public createResellerAsync = (reseller: Reseller, file?: File): Promise<Reseller> => {
        if (file) {
            return this.uploadWithFileAsync("/api/resellers", reseller, file);
        } else {
            return firstValueFrom(
                this.httpClient.post<Reseller>("/api/resellers", reseller, {
                    context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: "/api/resellers" })
                })
            );
        }
    };

    public updateResellerAsync = (reseller: Reseller, file?: File): Promise<Reseller> => {
        const url = `/api/resellers/${reseller.id}`;
        if (file) {
            return this.uploadWithFileAsync(url, reseller, file, "PUT");
        } else {
            return firstValueFrom(
                this.httpClient.put<Reseller>(url, reseller, {
                    context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: "/api/resellers" })
                })
            );
        }
    };

    public deleteResellerAsync = (resellerId: string): Promise<void> =>
        firstValueFrom(
            this.httpClient.delete<void>(`/api/resellers/${resellerId}`, {
                context: new HttpContext().set(CACHE_CONFIG, { invalidateCache: "/api/resellers" })
            })
        );

    public acceptEulaAsync = (resellerId: string): Promise<void> => firstValueFrom(this.httpClient.post<void>(`/api/resellers/${resellerId}/eula/accept`, {}));

    public getColorCodes(): ColorCode[] {
        return [
            { name: "White", hexCode: "#ffffff" },
            { name: "Light Grey", hexCode: "#e6e9ed" },
            { name: "Dark Grey", hexCode: "#3f4449" }
        ];
    }

    public getResellerTypes(): ResellerType[] {
        return [
            { key: "0", value: "Affiliate" },
            { key: "1", value: "Licensing" }
        ];
    }

    private uploadWithFileAsync(url: string, reseller: Reseller, file: File, method: string = "POST"): Promise<Reseller> {
        const formData = new FormData();
        formData.append("reseller", JSON.stringify(reseller));
        formData.append("file", file);

        const request = method === "PUT" ? this.httpClient.put<Reseller>(url, formData) : this.httpClient.post<Reseller>(url, formData);

        return firstValueFrom(request);
    }
}
